/**
 * Real API implementation for Facilities.
 * GET /api/v3/facilities – used by Set Organization Structure view (org from auth token).
 */

import { apiClient } from '../apiClient';
import { normalizePaginatedResponse } from '../utils/paginationResponseUtils';

const V3_FACILITIES_PATH = '/api/v3/facilities';
const V3_ORGANIZATIONS_PATH = '/api/v3/organizations';

function buildQueryString(params) {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '')
      q.append(key, String(value));
  });
  const s = q.toString();
  return s ? `?${s}` : '';
}

/**
 * @param {Object} params - { page, limit, search, signal }
 * @returns {Promise<Object>} response.data
 */
export const getFacilities = async (params = {}) => {
  const { page = 1, limit = 10, search = '', signal } = params;
  const url = `${V3_FACILITIES_PATH}${buildQueryString({ page, limit, search })}`;
  const response = await apiClient.get(url, { signal });
  return response.data;
};

/**
 * @param {Object} facilityData
 * @returns {Promise<Object>} response.data
 */
export const createFacility = async (facilityData) => {
  const response = await apiClient.post(V3_FACILITIES_PATH, facilityData);
  return response.data;
};

/**
 * @param {string} facilityId
 * @param {Object} facilityData
 * @returns {Promise<Object>} response.data
 */
export const updateFacility = async (facilityId, facilityData) => {
  const response = await apiClient.patch(
    `${V3_FACILITIES_PATH}/${facilityId}`,
    facilityData,
  );
  return response.data;
};

/**
 * @param {string} facilityId
 * @returns {Promise<Object>} response.data
 */
export const deleteFacility = async (facilityId) => {
  const response = await apiClient.delete(`${V3_FACILITIES_PATH}/${facilityId}`);
  return response.data;
};

/**
 * POST /api/v3/facilities/{facilityId}/duplicate
 * Request body must have: newName, includeLocations, includeFacilityStandards, includeLocationStandards.
 *
 * @param {string} facilityId
 * @param {Object} duplicateData - { newName, includeLocations?, includeFacilityStandards?, includeLocationStandards? }
 * @returns {Promise<Object>} response.data
 */
const DEFAULT_DUPLICATE_OPTIONS = {
  includeFacilityStandards: true,
  includeLocationStandards: true,
};

export const duplicateFacility = async (facilityId, duplicateData = {}) => {
  const body = {
    newName: (duplicateData.newName ?? '').trim(),
    includeLocations: Boolean(duplicateData.includeLocations !== false),
    ...DEFAULT_DUPLICATE_OPTIONS,
  };
  const response = await apiClient.post(
    `${V3_FACILITIES_PATH}/${facilityId}/duplicate`,
    body,
  );
  return response.data;
};

/**
 * @param {string} organizationId
 * @returns {Promise<Array>}
 */
export const getFacilitiesByOrganization = async (organizationId) => {
  if (organizationId == null || organizationId === '') return [];
  const response = await apiClient.get(
    `${V3_ORGANIZATIONS_PATH}/${organizationId}/facilities`,
  );
  return response.data?.data ?? response.data ?? [];
};

/**
 * GET /api/v3/facilities – list facilities (org from auth token). Used by Set Organization Structure view.
 *
 * @param {Object} params - { cursor (page number), limit, search, sortBy, sortOrder, signal }
 * @returns {Promise<{ data: Array, pagination: { nextCursor, totalCount } }>}
 */
export const getFacilitiesV3Paginated = async (params = {}) => {
  const {
    cursor,
    limit = 10,
    search,
    sortBy = 'createdAt',
    sortOrder = 'desc',
    signal,
  } = params;
  const page =
    cursor != null && cursor !== '' ? Math.max(1, Number(cursor)) : 1;
  const query = buildQueryString({
    page,
    limit,
    search,
    sort_by: sortBy,
    sort_order: sortOrder,
  });
  const response = await apiClient.get(`${V3_FACILITIES_PATH}${query}`, {
    signal,
  });
  const raw = response?.data ?? response;
  return normalizePaginatedResponse(raw, { pageSize: limit });
};

/**
 * @param {string} organizationId
 * @param {Object} params - { cursor, limit, search, sortBy, sortOrder, signal }
 * @returns {Promise<{ data: Array, pagination: Object }>}
 */
export const getFacilitiesPaginated = async (organizationId, params = {}) => {
  if (organizationId == null || organizationId === '')
    return { data: [], results: [], pagination: {} };
  const { cursor, limit = 10, search, sortBy, sortOrder, signal } = params;
  const query = buildQueryString({
    cursor,
    limit,
    search,
    sort_by: sortBy,
    sort_order: sortOrder,
  });
  const response = await apiClient.get(
    `${V3_ORGANIZATIONS_PATH}/${organizationId}/facilities${query}`,
    { signal },
  );
  return response.data;
};

/**
 * @param {string[]} facilityIds
 * @param {Object} updates
 * @returns {Promise<Array>}
 */
export const updateFacilitiesBulk = async (facilityIds, updates) => {
  const response = await apiClient.patch(`${V3_FACILITIES_PATH}/bulk`, {
    facility_ids: facilityIds,
    ...updates,
  });
  return response.data?.data ?? response.data ?? [];
};

export const getAllFacilities = async () => {
  const response = await apiClient.get(`${V3_FACILITIES_PATH}/`);
  return response.data;
};

export const getDataUnitsByFacility = async (facilityId) => {
  const response = await apiClient.get(
    `${V3_FACILITIES_PATH}/${facilityId}/data_units/`,
  );
  return response.data;
};

export const associateDeviceIdentifier = async (facilityId, deviceBaseId) => {
  const response = await apiClient.post(
    `${V3_FACILITIES_PATH}/${facilityId}/associate/`,
    { device_base_id: deviceBaseId },
  );
  return response.data;
};

export const disassociateDeviceIdentifier = async (
  facilityId,
  deviceBaseId,
) => {
  const response = await apiClient.delete(
    `${V3_FACILITIES_PATH}/${facilityId}/disassociate/`,
    { data: { device_base_id: deviceBaseId } },
  );
  return response.data;
};
